import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import '../../styles/marketing.css';
import MarketingNav from './MarketingNav';
import MarketingFooter from './MarketingFooter';
import { useScrollReveal } from './useScrollReveal';

interface MarketingLayoutProps {
  children: ReactNode;
  /** Which nav link to highlight. */
  activeNav?: 'platform' | 'features' | 'pricing';
  /** Render the cream nav (default true). */
  nav?: boolean;
  /** Render the navy footer (default true). */
  footer?: boolean;
  /** Re-run scroll-reveal when this changes (e.g. async content). */
  revealDeps?: ReadonlyArray<unknown>;
}

/**
 * Wraps marketing pages in the scoped `.clarid-marketing` design system,
 * loads the cream/navy nav + footer, and wires scroll-reveal. The scope
 * class keeps the "banker-paper" tokens from leaking into the authed app.
 */
export default function MarketingLayout({
  children,
  activeNav,
  nav = true,
  footer = true,
  revealDeps = [],
}: MarketingLayoutProps) {
  useScrollReveal(revealDeps);

  // React Router doesn't scroll to `#hash` anchors on its own, so a nav link like
  // "Platform" -> /#platform just set the URL and did nothing. Scroll the target into
  // view on hash/route change (handles same-page clicks AND cross-page navigation to a hash).
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => clearTimeout(t);
  }, [hash, pathname]);

  return (
    <div className="clarid-marketing">
      {nav && <MarketingNav active={activeNav} />}
      <main>{children}</main>
      {footer && <MarketingFooter />}
    </div>
  );
}
